import { Location } from '@angular/common';
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { BrandsService } from 'src/app/services/brands/brands.service';
import { FeeService } from 'src/app/services/fee/fee.service';
import {
  OneTimeRewardsRequest,
  OneTimeRewardsService,
} from 'src/app/services/one-time-rewards/one-time-rewards.service';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { StripeService } from '../../../services/stripe/stripe.service';
import { TeamsService } from '../../../services/teams/teams.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';

@Component({
    selector: 'app-one-time-reward-dialog',
    templateUrl: './team-one-time-reward.page.html',
    styleUrls: ['./team-one-time-reward.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeamOneTimeRewardComponent implements OnInit {
  public members: Array<any> = [];
  public form;
  public sendButtonLabel = 'No Members Selected';
  public team: any;
  public uploading = false;
  public isP2p: boolean;
  public title = '';
  public toTeam: any;
  public defaultPaymentMethod: any;
  public confirmationMessageP2p =
    'Make sure that this is what you intended to do rather than sending a one time reward to the allowance balance.';
  public confirmationMessageOnetime =
    'You may choose to either send the payment to their allowance balance towards purchases or directly to their Wallit balance where they can withdraw the funds to a connected bank account. You will be able to approve the payment before it is sent.';

  serviceFee = 0;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private stripeService: StripeService,
    private uiAlertService: UiAlertService,
    private teamsService: TeamsService,
    private location: Location,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<TeamOneTimeRewardComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TeamOneTimeRewardComponentModel,
    private feeService: FeeService,
    private readonly brandsService: BrandsService,
    private readonly spinnerService: SpinnerService,
    private readonly oneTimeRewardsService: OneTimeRewardsService,
  ) {
    this.isP2p = data.isP2p;
    this.team = data.team || data.toTeam;
    this.toTeam = data.toTeam || data.team;
    this.updateTitle();
    this.form = this.formBuilder.group({
      amount: new UntypedFormControl('', [Validators.required]),
      message: new UntypedFormControl('', [Validators.required]),
      note: new UntypedFormControl(''),
      destination: new UntypedFormControl(false, [Validators.required]),
      wallitBalance: new UntypedFormControl(false, [Validators.required]),
    });
    this.form.get('destination')?.setValue(false);
    this.teamsService.wellspaceChangedObservable.subscribe((wellspace) => {
      if (!this.team) {
        this.team = wellspace;
      }
      if (!this.toTeam) {
        this.toTeam = wellspace;
      }
      this.updateTitle();
    });
  }

  getPaymentMethod() {
    return this.form.get('wallitBalance')?.value ?? false;
  }

  getDestination() {
    return this.form.get('destination')?.value ?? false;
  }

  canSendStripeAllowanceToPendingMembers() {
    return !this.isP2p && !this.getDestination() && !this.getPaymentMethod();
  }

  async ngOnInit(): Promise<void> {
    if (this.canUseWallitBalance() && this.team) {
      this.spinnerService.show();
      if (this.team.defaultPaymentMethod) {
        const teamPaymentMethod = await this.teamsService.getPaymentMethod(this.team);
        const teamPaymentMethods = await this.teamsService.getPaymentMethods(this.team.id);
        this.defaultPaymentMethod = teamPaymentMethods.find(
          (thisMethod: { stripePaymentMethod: string }) =>
            thisMethod.stripePaymentMethod === teamPaymentMethod,
        );
      } else {
        this.form.get('wallitBalance')?.setValue(true);
      }
      this.spinnerService.hide();
    }
  }

  membersChanged(event: any): void {
    this.members = event;
    this.sendButtonLabel = `Send to ${this.members.length} Member${
      this.members.length === 1 ? '' : 's'
    }`;
  }

  setButtonCompletionMessage(count: number): void {
    this.uploading = true;
    this.sendButtonLabel = `${count} of ${this.members.length} Sent`;
  }

  async sendRewards(): Promise<void> {
    if (!this.team?.id || !this.toTeam?.id) {
      return;
    }

    const oneTimeRewardsRequest: OneTimeRewardsRequest = {
      amount: this.form.get('amount')?.value,
      message: this.form.get('message')?.value,
      note: this.form.get('note')?.value,
      destination: this.form.get('destination')?.value,
      recipients: this.members.map((member) => ({
        disabled: member.disabled,
        email: member.email || member.userEmail,
        inviteSentAt: member.inviteSentAt,
        message: member.message,
        metadata: member.metadata,
        removalStatus: member.removalStatus,
        status: member.status,
        teamUserId: member.memberId || member.teamUserId || member.id,
        userId: member.userId,
      })),
      isP2p: this.isP2p,
      team: this.team,
      toTeamId: this.toTeam.id,
      isUsingWallitBalance: this.form.get('wallitBalance')?.value,
    };

    const success = await this.oneTimeRewardsService.processOneTimeReward(
      oneTimeRewardsRequest,
      (count) => this.setButtonCompletionMessage(count),
    );

    if (success) {
      this.dialogRef.close(true);
    } else {
      this.uploading = false;
      this.dialogRef.close(false);
    }
  }

  canUseWallitBalance() {
    const brandConfig = this.brandsService.getConfigJSON();
    // TODO: When getConfigJSON is fixed, remove comment
    const brandHaveAccounts = true;// brandConfig.content?.ui?.tabs?.accounts;
    return !this.isP2p && brandHaveAccounts;
  }

  private updateTitle(): void {
    const usesExplicitDestinationTeam = !!this.data.toTeam && !this.data.team && !this.isP2p;
    const titlePrefix = this.isP2p
      ? 'Top Up Peer Recognition Balance for Team '
      : usesExplicitDestinationTeam
        ? 'Send Wallit-Funded Allowance to Team '
        : 'Send One Time Payment to Team ';
    this.title = `${titlePrefix}${this.toTeam?.name || ''}`;
  }

  onDismiss(): void {
    this.dialogRef.close(false);
  }
}

export class TeamOneTimeRewardComponentModel {
  constructor(public isP2p: boolean, public toTeam?: any, public team?: any) {}
}
